import React, { useEffect, useMemo, useState } from "react";
import "./sidebarTour.css";

const TOUR_KEY = "pp2_activities_tour_seen";

const STEPS = [
  {
    selector: '[data-tour="tab-mode"]',
    title: "Mode",
    text: "Choose Live or Self-Paced for this session.",
  },
  {
    selector: '[data-tour="tab-settings"]',
    title: "Settings",
    text: "Set points, timer behaviour, and activity-specific options.",
  },
  {
    selector: '[data-tour="tab-activities"]',
    title: "Activities",
    text: "Add and arrange your Quiz, Poll, Word Cloud, and Pin Drop activities here.",
  },
  {
    selector: '[data-tour="tab-review"]',
    title: "Review",
    text: "See a summary of the session before you run it.",
  },
  {
    selector: '[data-tour="present-btn"]',
    title: "Present",
    text: "When you're ready, open the presenter screen to run the session live.",
  },
];

/**
 * First-time, click-through guided tour for the session sidebar. Shown once
 * (persisted in localStorage) when the admin first lands on the Activities
 * page. Purely informational — a spotlight + tooltip walking through each
 * tab and the Present button. Does not let the user interact with the
 * underlying page while active.
 */
export default function SidebarTour() {
  const alreadySeen = useMemo(() => {
    try {
      return localStorage.getItem(TOUR_KEY) === "1";
    } catch {
      return false;
    }
  }, []);

  const [active, setActive] = useState(!alreadySeen);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);

  const step = STEPS[stepIndex];

  // Measure the target element's position for the current step.
  useEffect(() => {
    if (!active || !step) return undefined;

    const measure = () => {
      const el = document.querySelector(step.selector);
      if (el) {
        setRect(el.getBoundingClientRect());
      } else {
        setRect(null);
      }
    };

    measure();
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 50); // allow layout to settle
    return () => {
      window.removeEventListener("resize", measure);
      clearTimeout(t);
    };
  }, [active, step, stepIndex]);

  const finish = () => {
    try {
      localStorage.setItem(TOUR_KEY, "1");
    } catch {
      // ignore storage failures
    }
    setActive(false);
  };

  const next = () => {
    if (stepIndex >= STEPS.length - 1) {
      finish();
    } else {
      setStepIndex((i) => i + 1);
    }
  };

  const prev = () => {
    if (stepIndex > 0) setStepIndex((i) => i - 1);
  };

  if (!active) return null;

  const pad = 8;
  const spotlightStyle = rect
    ? {
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
      }
    : null;

  // Tooltip placed to the right of the target, falling back below if it
  // would overflow the viewport.
  const tooltipStyle = rect
    ? (() => {
        const tooltipWidth = 300;
        const spaceRight = window.innerWidth - (rect.right + 20);
        if (spaceRight > tooltipWidth) {
          return { top: rect.top, left: rect.right + 20 };
        }
        return { top: rect.bottom + 14, left: Math.max(16, rect.left) };
      })()
    : { top: "40%", left: "50%", transform: "translate(-50%, -50%)" };

  return (
    <div className="stour-overlay" onClick={(e) => e.stopPropagation()}>
      {spotlightStyle && <div className="stour-spotlight" style={spotlightStyle} />}

      <div className="stour-tooltip" style={tooltipStyle}>
        <div className="stour-step-count">
          {stepIndex + 1} / {STEPS.length}
        </div>
        <div className="stour-title">{step.title}</div>
        <p className="stour-text">{step.text}</p>
        <div className="stour-actions">
          <button className="stour-skip" onClick={finish}>
            Skip tour
          </button>
          <div className="stour-nav">
            {stepIndex > 0 && (
              <button className="stour-prev" onClick={prev}>
                Back
              </button>
            )}
            <button className="stour-next" onClick={next}>
              {stepIndex === STEPS.length - 1 ? "Done" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { TOUR_KEY };
